import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router";
import type { PropertyResponseType } from "@services/housien/types";
import { useDataStateOverride, applyDataStateOverride, computeFirstLoadGate } from "@/shared/ui";
import { useLoading } from "@context/LoadingContext";
import {
  useFetchPropertiesEffect,
  useInfiniteScroll,
} from "./use-properties-data.helpers";

/**
 * The only column key the server can order the property list by, and the
 * backend `order_by` value it maps to. The backend supports `sale_price` and
 * `rental_price`; the list's single `price` column orders on `sale_price`
 * (the primary figure a portfolio is ranked by). Any other column is NOT
 * server-sortable, so its header is left non-sortable rather than silently
 * client-sorting one accumulated page.
 */
const SERVER_SORT_BY_COLUMN: Record<string, string> = {
  price: 'sale_price',
};

/** The backend key for "when the listing was registered". */
const RECENCY_ORDER_BY = 'created_at';

export type PropertySortState = { order_by: string; order: 'asc' | 'desc' } | null;

type CrudSlice = {
  fetchId: unknown;
  setIsFormVisible: (v: boolean) => void;
  setIsTableVisible: (v: boolean) => void;
  advancedFilters: Record<string, any>;
  fetchSettings: { pageSize: number; page?: number } | undefined;
  setFetchSettings: (s: { pageSize: number; page?: number }) => void;
  totalPages: number;
  setTotalPages: (n: number) => void;
};

/**
 * Handles the one-time "editProperty" navigation state on mount.
 */
function useIncomingEdit(
  incoming: PropertyResponseType | null,
  setEditingProperty: (p: PropertyResponseType | null) => void,
  setIsFormVisible: (v: boolean) => void,
  pathname: string,
) {
  useEffect(() => {
    if (!incoming) return;
    setEditingProperty(incoming);
    setIsFormVisible(true);
    window.history.replaceState({}, '', pathname);
  }, []);
}

/**
 * Consumes an "action=new" search param to open the create form.
 */
function useNewActionParam(
  setEditingProperty: (p: PropertyResponseType | null) => void,
  setIsTableVisible: (v: boolean) => void,
  setIsFormVisible: (v: boolean) => void,
) {
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('action') !== 'new') return;
    setEditingProperty(null);
    setIsTableVisible(false);
    setIsFormVisible(true);
    const next = new URLSearchParams(searchParams);
    next.delete('action');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, setIsFormVisible, setIsTableVisible]);
}

/**
 * Resolves the next sort direction in the tri-state cycle (asc → desc → null).
 */
function nextSortDir(prev: { key: string | null; dir: 'asc' | 'desc' | null }, colKey: string): 'asc' | 'desc' | null {
  if (prev.key !== colKey) return 'asc';
  return prev.dir === 'asc' ? 'desc' : null;
}

/**
 * Resolves the server sort query from the tri-state column + direction, or
 * `null` when the current selection is not server-sortable.
 */
function resolveSortQuery(key: string | null, dir: 'asc' | 'desc' | null): PropertySortState {
  if (!key || !dir) return null;
  const orderBy = SERVER_SORT_BY_COLUMN[key];
  if (!orderBy) return null;
  return { order_by: orderBy, order: dir };
}

/**
 * Manages the list's ordering: the price column's tri-state header sort, and
 * the recency order the "Ordenar" menu picks.
 *
 * The two are one choice, not two: a list cannot be ordered by price and by
 * date at once. Picking a direction from the menu drops the column sort, and
 * clicking the price header takes over from the menu. With neither chosen the
 * list is newest-first -- the same default the backend applies when a request
 * names no ordering at all.
 */
function useSortState() {
  const [sortState, setSortState] = useState<{ key: string | null; dir: 'asc' | 'desc' | null }>({
    key: null,
    dir: null,
  });
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  const onSort = useCallback((col: { key: string; sortable?: boolean }) => {
    if (!col.sortable || !SERVER_SORT_BY_COLUMN[col.key]) return;
    setSortState((prev) => {
      const dir = nextSortDir(prev, col.key);
      return { key: dir ? col.key : null, dir };
    });
  }, []);

  const onOrderChange = useCallback((next: 'asc' | 'desc') => {
    setSortState({ key: null, dir: null });
    setOrder(next);
  }, []);

  // Memoised on the primitives: `sort` is a dependency of the fetch effect,
  // and a fresh object per render re-ran it on every render -- each run sets
  // loading state, which renders again, and the page froze in that loop.
  const columnSort = useMemo(
    () => resolveSortQuery(sortState.key, sortState.dir),
    [sortState.key, sortState.dir],
  );
  const sort = useMemo<PropertySortState>(
    () => columnSort ?? { order_by: RECENCY_ORDER_BY, order },
    [columnSort, order],
  );

  return {
    sortKey: sortState.key,
    sortDir: sortState.dir,
    sort,
    onSort,
    order: columnSort ? 'desc' : order,
    onOrderChange,
  };
}

/**
 * Tracks first-load completion and computes data-state flags.
 */
function useDataStateComputed(properties: PropertyResponseType[], isLoading: boolean, loadError: boolean) {
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  useEffect(() => {
    if (!isLoading) setHasLoadedOnce(true);
  }, [isLoading]);

  const dataStateOverride = useDataStateOverride();
  const effectiveProperties = dataStateOverride === 'empty' ? [] : properties;
  // Shared first-load split: the skeleton (and thus the header unmount) is
  // gated on the first load only, so a filter/search refetch keeps the header
  // and its search input mounted. See `computeFirstLoadGate`.
  const { showLoadingSkeleton, isRefreshing } = computeFirstLoadGate(isLoading, hasLoadedOnce);
  const dataStateFlags = applyDataStateOverride(dataStateOverride, {
    isLoading: showLoadingSkeleton,
    isError: loadError,
    isEmpty: properties.length === 0,
  });

  return { effectiveProperties, dataStateFlags, isRefreshing };
}

/**
 * Fetches and exposes the property list data.
 */
export function usePropertiesData(params: {
  crud: CrudSlice;
  agencyId: string | undefined;
  propertiesFilters: Record<string, any>;
  advancedFilters?: Record<string, any>;
  isMobile: boolean;
  /** Which cover size the rows carry; `card` while the grid is on. */
  cover?: 'card';
}) {
  const { crud, agencyId, propertiesFilters, isMobile, cover } = params;
  const { fetchId, setIsFormVisible, setIsTableVisible, fetchSettings, setFetchSettings, totalPages, setTotalPages } = crud;
  const advancedFilters = params.advancedFilters ?? crud.advancedFilters;
  const { addRequestLoading, removeRequestLoading } = useLoading((state) => state);

  const [properties, setProperties] = useState<PropertyResponseType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState<number | null>(null);

  const { sortKey, sortDir, sort, onSort, order, onOrderChange } = useSortState();
  const { effectiveProperties, dataStateFlags, isRefreshing } = useDataStateComputed(properties, isLoading, loadError);

  const location = useLocation();
  const incomingEditProperty = (location.state as { editProperty?: PropertyResponseType } | null)?.editProperty ?? null;
  const [editingProperty, setEditingProperty] = useState<PropertyResponseType | null>(incomingEditProperty);

  const sentinelRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useIncomingEdit(incomingEditProperty, setEditingProperty, setIsFormVisible, location.pathname);
  useNewActionParam(setEditingProperty, setIsTableVisible, setIsFormVisible);

  useFetchPropertiesEffect(
    { fetchId, agencyId, propertiesFilters, advancedFilters, fetchSettings, setFetchSettings, sort, cover },
    { setIsLoadingMore, setIsLoading, setLoadError, addRequestLoading, removeRequestLoading, setTotalPages, setTotalCount, setProperties },
  );

  useInfiniteScroll({
    sentinelRef, scrollContainerRef, isMobile, isLoading,
    isLoadingMore, totalPages, fetchSettings, setFetchSettings,
  });

  return {
    properties,
    effectiveProperties,
    dataStateFlags,
    isRefreshing,
    isLoadingMore,
    totalCount,
    editingProperty,
    setEditingProperty,
    sentinelRef,
    scrollContainerRef,
    sortKey,
    sortDir,
    onSort,
    order,
    onOrderChange,
  };
}
